import { useState } from "react";
import { Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import PageMeta from "@/components/PageMeta";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { usePortfolioReports, useSwyftxCredential } from "@/hooks/usePortfolioReport";
import { MorningReportCard } from "@/components/home/MorningReportCard";

export default function MyReport() {
  const { user, isLoading } = useAuth();
  const { data: cred } = useSwyftxCredential();
  const { data: reports, isLoading: loadingReports } = usePortfolioReports(14);
  const [selected, setSelected] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const qc = useQueryClient();

  if (isLoading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  if (!user) return (
    <div className="container mx-auto px-4 py-16 max-w-md text-center space-y-4">
      <h1 className="type-page-title">My portfolio report</h1>
      <p className="text-muted-foreground">Sign in and add your Swyftx read-only key to get a daily 6am report.</p>
      <Button asChild><Link to="/auth">Sign in</Link></Button>
    </div>
  );

  const report = reports?.find((r) => r.id === selected) ?? reports?.[0];

  const runNow = async () => {
    setRunning(true);
    const { data, error } = await supabase.functions.invoke("save-swyftx-key", { body: { action: "run_now" } });
    setRunning(false);
    if (error || data?.error) {
      let msg = data?.error;
      try { msg = msg ?? (await (error as any)?.context?.json())?.error; } catch { /* noop */ }
      return toast.error(msg ?? "Couldn't start the report.");
    }
    toast.success("Building your report — this takes about a minute.");
    setTimeout(() => qc.invalidateQueries({ queryKey: ["portfolio-reports"] }), 1500);
  };

  return (
    <div className="min-h-screen bg-background">
      <PageMeta title="My portfolio report | GoToCoins" description="Your daily AI portfolio review from live Swyftx data." path="/my-report" />
      <div className="container mx-auto px-4 py-8 max-w-3xl space-y-6">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h1 className="type-page-title">My portfolio report</h1>
          {cred && (
            <Button onClick={runNow} disabled={running} variant="outline" size="sm" className="gap-2">
              {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Run my report now
            </Button>
          )}
        </div>

        {!cred && (
          <Card><CardContent className="p-5 space-y-3 text-sm">
            <p>Add your Swyftx read-only API key on your account page to get a report every morning at 6am.</p>
            <Button asChild size="sm"><Link to="/account">Add my Swyftx key</Link></Button>
          </CardContent></Card>
        )}

        {loadingReports ? <Loader2 className="w-5 h-5 animate-spin" /> : !report ? (
          cred && <p className="text-sm text-muted-foreground">No report yet. Your first one arrives at 6am, or tap "Run my report now".</p>
        ) : (
          <>
            {report.id === reports?.[0]?.id && <MorningReportCard />}
            {report.status === "failed" && (
              <Card className="border-destructive/40"><CardContent className="p-4 text-sm">
                This report couldn't be built: {report.error}
              </CardContent></Card>
            )}
            {report.markdown && (
              <article className="prose prose-invert max-w-none prose-table:text-sm">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{report.markdown}</ReactMarkdown>
              </article>
            )}
          </>
        )}

        {reports && reports.length > 1 && (
          <div className="space-y-2">
            <h2 className="type-eyebrow">Past reports</h2>
            <div className="flex flex-wrap gap-2">
              {reports.map((r) => (
                <Button key={r.id} size="sm" variant={r.id === report?.id ? "default" : "outline"} onClick={() => setSelected(r.id)}>
                  {new Date(r.report_date).toLocaleDateString("en-AU", { day: "numeric", month: "short" })}
                </Button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
